import { useEffect, useRef } from "react";

export default function TransparentProductVideo({ src, style }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d", {
      willReadFrequently: true,
    });

    const offscreen = document.createElement("canvas");
    const offCtx = offscreen.getContext("2d", {
      willReadFrequently: true,
    });

    let frameW = 0;
    let halfH = 0;

    let output = null;
    let outputData = null;

    let rafId = null;
    let videoFrameId = null;

    let rendering = false;
    let destroyed = false;

    const setup = () => {
      if (!video.videoWidth || !video.videoHeight) return;

      frameW = video.videoWidth;
      halfH = Math.floor(video.videoHeight / 2);

      offscreen.width = frameW;
      offscreen.height = video.videoHeight;

      canvas.width = frameW;
      canvas.height = halfH;

      // Create this ONCE instead of every frame
      output = ctx.createImageData(frameW, halfH);
      outputData = output.data;
    };

    const drawFrame = () => {
      if (
        destroyed ||
        !output ||
        video.readyState < 2 ||
        video.paused ||
        video.ended
      ) {
        return;
      }

      offCtx.drawImage(
        video,
        0,
        0,
        frameW,
        video.videoHeight
      );

      const full = offCtx.getImageData(
        0,
        0,
        frameW,
        video.videoHeight
      );

      const data = full.data;

      const rowBytes = frameW * 4;
      const alphaOffset = halfH * rowBytes;

      for (let y = 0; y < halfH; y++) {
        const colorRow = y * rowBytes;
        const alphaRow = alphaOffset + y * rowBytes;

        for (let x = 0; x < frameW; x++) {
          const i = colorRow + x * 4;
          const ai = alphaRow + x * 4;

          outputData[i] = data[i];
          outputData[i + 1] = data[i + 1];
          outputData[i + 2] = data[i + 2];

          // Use red channel from mask
          outputData[i + 3] = data[ai];
        }
      }

      ctx.putImageData(output, 0, 0);
    };

    const renderRAF = () => {
      if (!rendering || destroyed) return;

      drawFrame();

      rafId = requestAnimationFrame(renderRAF);
    };

    const renderVideoFrame = () => {
      if (!rendering || destroyed) return;

      drawFrame();

      if (video.requestVideoFrameCallback) {
        videoFrameId =
          video.requestVideoFrameCallback(renderVideoFrame);
      }
    };

    const startRendering = () => {
      if (rendering || destroyed) return;

      rendering = true;

      if (video.requestVideoFrameCallback) {
        videoFrameId =
          video.requestVideoFrameCallback(renderVideoFrame);
      } else {
        rafId = requestAnimationFrame(renderRAF);
      }
    };

    const stopRendering = () => {
      rendering = false;

      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (
        videoFrameId &&
        video.cancelVideoFrameCallback
      ) {
        video.cancelVideoFrameCallback(videoFrameId);
        videoFrameId = null;
      }
    };

    const playVideo = async () => {
      if (destroyed) return;

      try {
        video.muted = true;
        video.playsInline = true;

        await video.play();

        startRendering();
      } catch (error) {
        console.log("Waiting for video playback...");
      }
    };

    const handleMetadata = () => {
      setup();
    };

    const handleCanPlay = () => {
      playVideo();
    };

    const handlePlay = () => {
      startRendering();
    };

    const handlePause = () => {
      stopRendering();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        playVideo();
      } else {
        stopRendering();
      }
    };

    const handlePageShow = () => {
      playVideo();
    };

    video.addEventListener(
      "loadedmetadata",
      handleMetadata
    );

    video.addEventListener(
      "canplay",
      handleCanPlay
    );

    video.addEventListener(
      "play",
      handlePlay
    );

    video.addEventListener(
      "pause",
      handlePause
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibility
    );

    window.addEventListener(
      "pageshow",
      handlePageShow
    );

    // Start loading
    video.load();

    playVideo();

    return () => {
      destroyed = true;

      stopRendering();

      video.pause();

      video.removeEventListener(
        "loadedmetadata",
        handleMetadata
      );

      video.removeEventListener(
        "canplay",
        handleCanPlay
      );

      video.removeEventListener(
        "play",
        handlePlay
      );

      video.removeEventListener(
        "pause",
        handlePause
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibility
      );

      window.removeEventListener(
        "pageshow",
        handlePageShow
      );
    };
  }, [src]);

  return (
    <div style={style}>
      <video
        ref={videoRef}
        src={src}
        muted
        autoPlay
        loop
        playsInline
        preload="auto"
        style={{
          display: "none",
        }}
      />

      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />
    </div>
  );
}